/**
 * src/data/SalaryNegotiationBank.js
 * Hardcoded scenario bank for Module 7: Salary Negotiation (Adaptive Adversary Engine)
 */

export const salaryNegotiationBank = [
  {
    "id": "salary_01",
    "scenarioContext": "The recruiter just offered you a $75,000 base salary. This is $5k below your target, but it includes a 6% 401k match, fully paid healthcare, and an annual $5k education stipend.",
    "recruiterPrompt": "RECRUITER: 'We are thrilled to offer you $75,000 base. Because you are entry-level, we are locked tightly to this band. How does this package look to you?'",
    "options": [
      {
        "responseText": "The benefits package is incredibly strong. While $75k is slightly below my target, considering the 6% match and healthcare coverage, I am thrilled to accept.",
        "category": "Optimal",
        "feedback": "Correct. Financial literacy. You correctly calculated the 'Total Rewards' package (valuing the 401k and healthcare) rather than blindly rejecting the base salary."
      },
      {
        "responseText": "I really need $80,000 in base salary to make rent in this city. Can you please just bump up the cash? I don't care about the education stipend.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. You ignored thousands of dollars in total compensation because of a short-term cash fixation, demonstrating poor financial modeling."
      },
      {
        "responseText": "If you can't hit $85,000 base, I have three other startups offering me that right now. Match it or I walk.",
        "category": "Failure",
        "feedback": "Failure. Unprofessional ultimatum. Lying about competing offers or threatening a corporate recruiter will result in the immediate rescission of your offer."
      },
      {
        "responseText": "Okay, that's fine. I will take it.",
        "category": "Failure",
        "feedback": "Failure. While you accepted the offer, failing to even clarify the terms or attempt a soft counter-offer demonstrates zero negotiation leverage."
      }
    ]
  },
  {
    "id": "salary_02",
    "scenarioContext": "You are offered a $110,000 base salary, which is $15k below your request, but the offer includes $50,000 in Restricted Stock Units (RSUs) vesting over 4 years.",
    "recruiterPrompt": "RECRUITER: 'We can't meet the $125k base, but we are offering a very competitive $110k plus $50k in RSUs. Does this align with your expectations?'",
    "options": [
      {
        "responseText": "I appreciate the RSU grant. If the $110k base is a hard ceiling, could we explore increasing the RSU grant to $65k to bridge the total compensation gap?",
        "category": "Optimal",
        "feedback": "Correct. Strategic leverage. You recognized the base ceiling and pivoted to negotiating the equity pool, maximizing total compensation."
      },
      {
        "responseText": "RSUs don't pay the bills today. I need you to convert that $50k in stock into a higher base salary.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Dismissing enterprise equity as worthless displays a lack of financial literacy and ignores the company's long-term growth incentives."
      },
      {
        "responseText": "This is a lowball offer. I won't accept anything less than $125k base, period. Let me know if you can get serious.",
        "category": "Failure",
        "feedback": "Failure. Hostile and inflexible. Delivering a 'take it or leave it' demand over a standard compensation structure burns the bridge entirely."
      },
      {
        "responseText": "I guess $110k is okay. I will sign the offer letter today.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You folded immediately without testing the flexibility of the equity or signing bonus, leaving money on the table."
      }
    ]
  },
  {
    "id": "salary_03",
    "scenarioContext": "The company offers you a $95,000 base salary instead of your $105,000 target, but includes a $10,000 sign-on bonus.",
    "recruiterPrompt": "RECRUITER: 'We are capped at $95k for this level, but we added a $10k sign-on bonus to bridge the gap for your first year. How does that sound?'",
    "options": [
      {
        "responseText": "The sign-on bonus is generous and helps bridge year one. To align year two, could we agree to an early 6-month performance review tied to a salary adjustment?",
        "category": "Optimal",
        "feedback": "Correct. Long-term strategic thinking. You accepted the short-term cash fix while securing a structural path to your long-term base salary goal."
      },
      {
        "responseText": "A sign-on bonus is just a one-time gimmick. I need the $105k base or I'm losing money long-term.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. While factually true regarding year two, calling a $10k bonus a 'gimmick' is unprofessional and fixates purely on the base number."
      },
      {
        "responseText": "Make the sign-on bonus $25k right now and I'll sign. Otherwise, I'm declining.",
        "category": "Failure",
        "feedback": "Failure. Extorting the recruiter by demanding more than double the bonus with a strict timeline is toxic negotiation behavior."
      },
      {
        "responseText": "That makes sense. Thank you for the extra $10k, I accept.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You failed to realize that your year-two salary will drop back to $95k and surrendered without securing future guarantees."
      }
    ]
  },
  {
    "id": "salary_04",
    "scenarioContext": "You are transitioning to a sales role. The offer is $60,000 base with $80,000 On-Target Earnings (OTE) in commission. You wanted an $80,000 base.",
    "recruiterPrompt": "RECRUITER: 'Our sales org relies heavily on variable pay to drive performance. $60k base is standard, but top performers hit $140k total easily. Are you comfortable with this?'",
    "options": [
      {
        "responseText": "I understand the variable structure. To mitigate the ramp-up period, would the company consider a non-recoverable draw or a guaranteed commission floor for my first 90 days?",
        "category": "Optimal",
        "feedback": "Correct. Financial literacy. You accepted the commission structure but astutely negotiated a risk-mitigation strategy for your onboarding period."
      },
      {
        "responseText": "I can't take the risk of commission right away. I need an $80k base so I know exactly what my paycheck will be every month.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Refusing variable pay in a sales role shows you are risk-averse and completely misaligned with the role's fundamental incentive structure."
      },
      {
        "responseText": "I won't work for $60k. Give me $80k base and lower my commission rate, or I'm taking another offer.",
        "category": "Failure",
        "feedback": "Failure. Arrogant and uncompromising. You attempted to dictate the company's entire sales compensation philosophy via threat."
      },
      {
        "responseText": "Sure, I guess I'll just have to sell a lot to make my target.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You accepted the risk fully without attempting to negotiate accelerators, ramp-up draws, or quota relief."
      }
    ]
  },
  {
    "id": "salary_05",
    "scenarioContext": "The company offers you $105,000 but mandates 5 days in the office. You currently make $100,000 working fully remote and highly value your time.",
    "recruiterPrompt": "RECRUITER: 'The offer is $105,000. We do require 5 days on-site, as our CEO strongly believes in in-person collaboration. How do you feel about the package?'",
    "options": [
      {
        "responseText": "I value in-person collaboration, but the commute adds significant overhead. If 5 days on-site is firm, could we increase the base to $115k to offset the transit and time costs?",
        "category": "Optimal",
        "feedback": "Correct. Strategic leverage. You quantified the cost of the RTO mandate and used it as legitimate leverage to increase your base salary."
      },
      {
        "responseText": "I just want to maximize my cash. If you can do $120k, I don't care how many days I have to come in.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Pure cash fixation. You blindly chased the dollar amount without considering the massive lifestyle and time-cost implications of the commute."
      },
      {
        "responseText": "I'm not doing 5 days a week. It's 2024. I will do 2 days on-site maximum or I'm withdrawing my application.",
        "category": "Failure",
        "feedback": "Failure. Combative and out of touch. Directly insulting the CEO's policy and delivering an ultimatum ends the negotiation instantly."
      },
      {
        "responseText": "That's fine, I can commute 5 days a week for the $105k.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You gave up a massive lifestyle benefit (remote work) for a negligible 5% pay bump without pushing for commuter benefits or higher pay."
      }
    ]
  },
  {
    "id": "salary_06",
    "scenarioContext": "You interviewed for a Senior Developer role, but they 'down-leveled' you to Mid-Level in the offer, offering $120k instead of the $145k Senior band.",
    "recruiterPrompt": "RECRUITER: 'The team loved you, but felt you needed a bit more system-design experience. We are offering you a Mid-Level role at $120k, the top of that band.'",
    "options": [
      {
        "responseText": "I appreciate the transparency. If I join at the Mid-Level, can we build a concrete, metric-driven roadmap into my 90-day plan to fast-track my promotion to Senior?",
        "category": "Optimal",
        "feedback": "Correct. Strategic leverage. You accepted the immediate reality but negotiated a structured, accelerated path to reclaim the higher title and pay."
      },
      {
        "responseText": "If you can't give me the Senior title, you at least need to pay me $140k in cash. I won't take a pay cut based on a bad interview.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Cash fixation. You ignored the recruiter explicitly stating $120k is the top of the band and demanded an impossible cash exception."
      },
      {
        "responseText": "This is insulting. I applied for a Senior role. Give me the Senior title and pay or I'm leaving.",
        "category": "Failure",
        "feedback": "Failure. Ego-driven ultimatum. Refusing to accept objective interview feedback and acting insulted is highly unprofessional."
      },
      {
        "responseText": "Okay, I understand. I will take the Mid-Level role at $120k.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You accepted a demotion in scope and pay without securing any guarantees or timelines for future advancement."
      }
    ]
  },
  {
    "id": "salary_07",
    "scenarioContext": "You are offered $85,000, which is below market rate, but the company offers a 4-day work week.",
    "recruiterPrompt": "RECRUITER: 'We know $85k is slightly below market, but we operate on a strict 32-hour, 4-day work week. We heavily value work-life balance.'",
    "options": [
      {
        "responseText": "The 32-hour week is a massive benefit. Since the base is lower, could we explore adding a performance-based annual bonus to bridge the gap if I hit my KPIs?",
        "category": "Optimal",
        "feedback": "Correct. Strategic leverage. You valued the time-based benefits accurately, but smartly pushed for a variable cash upside tied to your output."
      },
      {
        "responseText": "I don't care about a 4-day week. I want to work 5 days and get paid $105k in pure cash.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Cash fixation. You explicitly rejected the company's core operating culture (work-life balance) just to chase a higher liquid number."
      },
      {
        "responseText": "I need $100k or I'm not signing. Four day weeks are just an excuse to underpay talent.",
        "category": "Failure",
        "feedback": "Failure. Hostile and cynical. Insulting the company's benefits package and issuing an ultimatum is a guaranteed way to lose the offer."
      },
      {
        "responseText": "Wow, a 4-day week sounds amazing. I accept the $85k.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You were mesmerized by the perk and completely failed to negotiate equity, signing bonuses, or performance metrics."
      }
    ]
  },
  {
    "id": "salary_08",
    "scenarioContext": "The recruiter gives you an 'exploding offer' of $115,000, demanding an answer within 24 hours.",
    "recruiterPrompt": "RECRUITER: 'We think you're great, and we are offering $115,000. However, we have other candidates waiting, so we need a signed response by 5 PM tomorrow.'",
    "options": [
      {
        "responseText": "I'm very excited about the offer. However, this is a major life decision. I will need until Monday at 10 AM to properly review the total rewards package before signing.",
        "category": "Optimal",
        "feedback": "Correct. Strategic leverage. You professionally but firmly pushed back on an artificial, high-pressure deadline to protect your decision-making leverage."
      },
      {
        "responseText": "If you want an answer in 24 hours, you need to pay me $125,000. Otherwise, I need more time.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Extortionate cash fixation. Trying to hold a timeline hostage for a sudden cash increase is unprofessional and will likely backfire."
      },
      {
        "responseText": "An exploding offer is a huge red flag. I don't respond to threats. You can give me a week or rescind it.",
        "category": "Failure",
        "feedback": "Failure. Defensive and confrontational. While exploding offers are aggressive, attacking the recruiter ensures the offer will be rescinded immediately."
      },
      {
        "responseText": "Okay, I don't want to lose the spot, so I will sign it right now.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You surrendered to a high-pressure sales tactic, abandoning all negotiation power and potentially missing out on better terms."
      }
    ]
  },
  {
    "id": "salary_09",
    "scenarioContext": "You are leaving $25,000 in unvested stock at your current job. The new company is offering a great base salary of $140,000 but zero signing bonus.",
    "recruiterPrompt": "RECRUITER: 'We are thrilled to offer you $140,000 base, which is a 20% increase from your current salary. Are you ready to accept?'",
    "options": [
      {
        "responseText": "The $140k base is strong. However, I am leaving $25k in unvested equity on the table to join you. Can we include a sign-on bonus to help offset that specific forfeiture?",
        "category": "Optimal",
        "feedback": "Correct. Strategic leverage. You used objective financial data (unvested equity) to logically justify the request for a sign-on bonus without being aggressive."
      },
      {
        "responseText": "The base is good, but I just need more cash. Can we make it $150k instead? I have a lot of expenses coming up.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Cash fixation. You begged for cash based on personal expenses rather than using your unvested equity as legitimate business leverage."
      },
      {
        "responseText": "You have to cover the $25k I'm losing at my old job. If you don't pay me a $25k bonus today, I'm staying where I am.",
        "category": "Failure",
        "feedback": "Failure. Hostile ultimatum. The new company is not obligated to pay your old company's equity. Demanding it as a hostage negotiation is highly unprofessional."
      },
      {
        "responseText": "Yes, a 20% bump is great. I will accept the $140k.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You literally threw $25,000 in the trash by failing to even ask if the new company had a mechanism to buy out your unvested equity."
      }
    ]
  },
  {
    "id": "salary_10",
    "scenarioContext": "You have a competing offer for $130,000. This company, which you prefer, is offering $120,000.",
    "recruiterPrompt": "RECRUITER: 'We are prepared to offer you $120,000. We really want you on the team. How does this sound?'",
    "options": [
      {
        "responseText": "I strongly prefer this team. However, I have a competing offer in hand for $130k. If we can bridge the gap to $125k base and a small sign-on, I will sign with you today.",
        "category": "Optimal",
        "feedback": "Correct. Elite leverage. You used a competing offer perfectly: you reaffirmed your preference for them, provided a specific counter, and offered an immediate close."
      },
      {
        "responseText": "Company X is giving me $130k in pure cash. If you can't give me $135k in cash right now, I'm going with them.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Greedy cash fixation. Trying to start a bidding war and demanding they beat the other offer rather than match it shows bad faith."
      },
      {
        "responseText": "I have another offer for $130k. Match it immediately or I'm hanging up.",
        "category": "Failure",
        "feedback": "Failure. Toxic arrogance. Weaponizing a competing offer with a rude ultimatum will cause the recruiter to tell you to take the other job."
      },
      {
        "responseText": "I actually have another offer for $130k, but I like you guys more, so I'll just take the $120k.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You held the strongest piece of leverage in the world (a higher competing offer) and threw it away out of fear of conflict."
      }
    ]
  },
  {
    "id": "salary_11",
    "scenarioContext": "You are interviewing at an early-stage startup. They offer you $85,000 (well below market) but 2% equity in the company.",
    "recruiterPrompt": "RECRUITER: 'As a seed-stage startup, we are lean on cash. We can offer $85k base, but a massive 2% equity grant. You'll be rich when we IPO.'",
    "options": [
      {
        "responseText": "I believe in the vision and the equity upside. To ensure I can comfortably cover my living expenses during this high-risk phase, could we adjust to $95k base and 1.5% equity?",
        "category": "Optimal",
        "feedback": "Correct. Strategic risk management. You rebalanced the risk profile of the offer, trading speculative equity for a livable base salary."
      },
      {
        "responseText": "I can't pay my mortgage with fake startup equity. I need a $115k base salary, keep the equity.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Total cash fixation. Calling their equity 'fake' insults the founders and entirely misses the point of joining an early-stage startup."
      },
      {
        "responseText": "If you don't have the cash to pay market rate, you shouldn't be hiring. I'm passing on this.",
        "category": "Failure",
        "feedback": "Failure. Unprofessional insult. While you are free to decline, mocking a startup's capitalization strategy is immature."
      },
      {
        "responseText": "2% sounds amazing! I will take the $85k and ride it to the IPO.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You blindly accepted a massive financial risk based on a recruiter's promise of future riches, without attempting to negotiate a safety net."
      }
    ]
  },
  {
    "id": "salary_12",
    "scenarioContext": "The company offers $105k, but the health insurance premiums are exceptionally high, effectively costing you $7k a year out of pocket.",
    "recruiterPrompt": "RECRUITER: 'We are offering a $105,000 base salary. Our benefits package is standard across the company. Does this work for you?'",
    "options": [
      {
        "responseText": "I noticed the healthcare premiums are higher than market average, reducing my net take-home. Could we adjust the base to $112k to offset this specific out-of-pocket cost?",
        "category": "Optimal",
        "feedback": "Correct. Elite financial literacy. You calculated your true net income and used objective data (premium costs) to rationally justify a base increase."
      },
      {
        "responseText": "I just feel like $105k isn't enough cash. I want $115k so I can have more money in my bank account every month.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Cash fixation. You failed to use the high premium cost as leverage, instead making a generic, weak plea for more liquid cash."
      },
      {
        "responseText": "Your health insurance is a joke. If you don't pay 100% of my premiums, I'm walking away.",
        "category": "Failure",
        "feedback": "Failure. Hostile ultimatum. Insulting corporate benefits and demanding a structural change to their HR policy is an impossible, toxic demand."
      },
      {
        "responseText": "The $105k base looks great. I accept.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You failed to read the total rewards package carefully and effectively accepted a $7,000 pay cut without a fight."
      }
    ]
  },
  {
    "id": "salary_13",
    "scenarioContext": "You are offered an $85,000 base salary, but the role requires 50% overnight travel. Your target was $100,000.",
    "recruiterPrompt": "RECRUITER: 'The offer is $85,000. As discussed, you will be on the road Monday through Wednesday every week. How does this look?'",
    "options": [
      {
        "responseText": "Given the 50% travel requirement and the personal time commitment that entails, I would need a base closer to $95k to make this sustainable long-term. Is there flexibility there?",
        "category": "Optimal",
        "feedback": "Correct. Strategic leverage. You successfully linked the severe lifestyle cost (50% travel) to a logical demand for higher compensation."
      },
      {
        "responseText": "I need $100k in cash. I don't really care about the travel, I just need to hit my six-figure goal.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Cash fixation. You entirely ignored the massive leverage of the travel requirement and just begged to cross a psychological cash threshold."
      },
      {
        "responseText": "I'm not traveling 50% of the time for only $85k. Cut the travel to 20% or I'm declining.",
        "category": "Failure",
        "feedback": "Failure. Ultimatum. You attempted to rewrite the core job description rather than negotiating the compensation for doing the job."
      },
      {
        "responseText": "That's fine. I don't mind traveling. I will accept the $85k.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You gave away half of your personal life for free. Failing to monetize heavy travel requirements is a massive negotiation failure."
      }
    ]
  },
  {
    "id": "salary_14",
    "scenarioContext": "The recruiter asks for your salary expectations upfront before revealing the company's budget for the role.",
    "recruiterPrompt": "RECRUITER: 'Before we move to the final round, I need to know your exact salary expectations so I can see if we are aligned.'",
    "options": [
      {
        "responseText": "I'm currently flexible and more focused on finding the right fit. Could you share the approved budget range for this role so I can see if we are in the same ballpark?",
        "category": "Optimal",
        "feedback": "Correct. Strategic leverage. You deflected the trap perfectly and forced the company to anchor the negotiation first, protecting your leverage."
      },
      {
        "responseText": "I need exactly $125,000 in base cash. If you can't pay that, we shouldn't waste our time.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Cash fixation. You anchored yourself immediately to a single cash number, completely ignoring equity, bonuses, and potential negotiation upside."
      },
      {
        "responseText": "I'm not going to play games. You tell me what you pay first, or I'm ending the interview.",
        "category": "Failure",
        "feedback": "Failure. Hostile and aggressive. While you should deflect the question, turning it into a combative standoff ruins your professional reputation."
      },
      {
        "responseText": "I'm currently making $85k, so I would be happy with anything around $90k.",
        "category": "Failure",
        "feedback": "Failure. Total capitulation. You revealed your current salary and lowballed yourself, guaranteeing the recruiter will offer you the absolute minimum."
      }
    ]
  },
  {
    "id": "salary_15",
    "scenarioContext": "The company offers $110,000, but the offer includes a strict 2-year non-compete clause that limits your future career mobility.",
    "recruiterPrompt": "RECRUITER: 'The offer is $110,000 base. Please note our standard contract includes a 24-month non-compete in this industry. Are you ready to proceed?'",
    "options": [
      {
        "responseText": "A 24-month non-compete presents a significant restriction on my future career mobility. Would the company be open to reducing it to 6 months, or increasing the base to $125k to offset that risk?",
        "category": "Optimal",
        "feedback": "Correct. Strategic leverage. You accurately assessed the career cost of a non-compete and used it to extract either legal concessions or a higher risk premium."
      },
      {
        "responseText": "I don't care what the contract says, just give me $120k in cash right now and I'll sign whatever you want.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Reckless cash fixation. Blindly signing severe legal restrictions just to get a slight cash bump demonstrates terrible long-term judgment."
      },
      {
        "responseText": "Non-competes are practically illegal now. I refuse to sign it. Take it out or I'm gone.",
        "category": "Failure",
        "feedback": "Failure. Hostile legal ultimatum. Playing amateur lawyer and refusing standard paperwork aggressively will cause the recruiter to drop you."
      },
      {
        "responseText": "That's standard for the industry. I will accept the $110k.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You surrendered your future career mobility without asking for a single dollar in compensation or attempting to reduce the timeline."
      }
    ]
  },
  {
    "id": "salary_16",
    "scenarioContext": "You are offered $90,000 and a 'Manager' title. You wanted $105,000. The recruiter claims the budget is absolutely maxed out.",
    "recruiterPrompt": "RECRUITER: 'I went to the CFO, and $90k is truly the absolute maximum we can offer. There is no more money. What do you say?'",
    "options": [
      {
        "responseText": "If the cash is truly capped at $90k, could we explore elevating the title to 'Director' to better reflect the scope of work I'll be taking on?",
        "category": "Optimal",
        "feedback": "Correct. Strategic leverage. When cash is mathematically exhausted, you intelligently pivoted to negotiating title equity, which has immense future resume value."
      },
      {
        "responseText": "There is always more money. Go back to the CFO and tell them I need $100k in cash or I'm not signing.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Delusional cash fixation. When a recruiter states a hard financial ceiling, stubbornly repeating your cash demand makes you look completely deaf."
      },
      {
        "responseText": "If you can't pay market rate, this company is clearly struggling. I rescind my application.",
        "category": "Failure",
        "feedback": "Failure. Unprofessional and insulting. Trashing the company's financial health because they couldn't meet your number is toxic behavior."
      },
      {
        "responseText": "Okay, if that's the absolute max, I will take it.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You hit a financial wall and gave up, failing to negotiate alternative value levers like title, PTO, or accelerated reviews."
      }
    ]
  },
  {
    "id": "salary_17",
    "scenarioContext": "The company offers a $115k base, but explicitly states that bonuses are '100% discretionary' based on company performance, not individual metrics.",
    "recruiterPrompt": "RECRUITER: 'The base is $115k. We also have an annual bonus target of 20%, though it is fully discretionary based on overall company profitability.'",
    "options": [
      {
        "responseText": "Given that the bonus is fully discretionary and out of my direct control, I have to discount its guaranteed value. Could we increase the base to $120k to ensure a more predictable baseline?",
        "category": "Optimal",
        "feedback": "Correct. Elite financial literacy. You correctly identified that a discretionary bonus carries massive risk, and used that to negotiate a higher guaranteed base."
      },
      {
        "responseText": "I love the 20% bonus, but I just want more guaranteed cash every paycheck. Make the base $130k.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Pure greed without leverage. You ignored the mechanical risk of the discretionary bonus and simply asked for a massive, unjustified cash bump."
      },
      {
        "responseText": "Discretionary bonuses are a scam. I demand that my 20% bonus be guaranteed in writing in the contract.",
        "category": "Failure",
        "feedback": "Failure. Unrealistic ultimatum. Demanding a company rewrite its entire enterprise bonus structure specifically for you is arrogant and impossible."
      },
      {
        "responseText": "That sounds fair. A 20% bonus is great upside, I accept the $115k.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You treated 'discretionary' money as guaranteed money. You accepted the risk entirely without attempting to secure your baseline."
      }
    ]
  },
  {
    "id": "salary_18",
    "scenarioContext": "You are offered a $55,000 base salary. The recruiter is using an aggressive high-pressure sales tactic to force an immediate verbal acceptance.",
    "recruiterPrompt": "RECRUITER: 'We love you! But we need an answer on the $55k salary by 5 PM today or we move to the next candidate.'",
    "options": [
      {
        "responseText": "I'm thrilled about the offer, but this is a major decision. I need 24 hours to review the written contract details before committing.",
        "category": "Optimal",
        "feedback": "Correct. Power Move. You absorbed the bullying tactic, maintained enthusiasm, and bought yourself time. If they want you, they will wait 24 hours."
      },
      {
        "responseText": "Can you bump it to $60k? If you do, I'll say yes right now over the phone.",
        "category": "Suboptimal",
        "feedback": "Suboptimal. Cash fixation. Trying to counter-offer verbally while under extreme pressure usually leads to bad deals and missed benefits."
      },
      {
        "responseText": "Exploding offers are unprofessional. You can't force me to decide in 5 hours. Give me until Monday.",
        "category": "Failure",
        "feedback": "Failure. Combative. While the tactic is aggressive, openly accusing the recruiter of being unprofessional will result in them pulling the offer."
      },
      {
        "responseText": "Okay, I can't risk losing the job. I accept the $55k.",
        "category": "Failure",
        "feedback": "Failure. Capitulation. You surrendered all leverage instantly. Giving in to an exploding offer proves their bullying tactics work."
      }
    ]
  }
];